/**
 * Thin, safe wrapper around the analytics snippets in public/index.html.
 *
 * TWO SINKS, DELIBERATELY ASYMMETRIC.
 *
 *   PostHog  gets the event and its full property bag. It is the product
 *            analytics tool and the funnel lives there.
 *   GA4      gets the event NAME and nothing else, and only for the five
 *            names on GA4_EVENTS below. It exists to make KEY EVENTS
 *            non-zero so conversions are countable next to acquisition.
 *
 * Every call is a no-op when the respective snippet hasn't loaded (ad
 * blockers, script failure, tests), so product code can call these
 * unconditionally.
 *
 * Privacy rule: customer URLs never leave the client in the clear —
 * only a hashed domain is sent (see hashDomain). Do not add raw URLs,
 * page paths of audited sites, or emails to event properties.
 */

const posthog = () => (typeof window !== 'undefined' && window.posthog) || null;

/**
 * PostHog event name -> GA4 event name. THIS MAP IS THE WHOLE GATE.
 *
 * An event absent from this map never reaches GA4, so adding a PostHog
 * event is not a decision to send it to Google — that is a second,
 * deliberate edit here. report_view_summary and audit_completed are
 * absent on purpose: they carry scroll depth, durations and scores that
 * GA4 has no use for and that we are not sending.
 *
 * The GA4 names are the owner's, and they differ from the PostHog names
 * because the PostHog names came first and are already in saved insights.
 * Renaming there to match here would break those; mapping costs one line.
 */
const GA4_EVENTS = {
  audit_started: 'audit_started',
  report_viewed: 'audit_delivered',
  paywall_hit: 'paywall_viewed',
  upgrade_clicked: 'upgrade_clicked',
  purchase_completed: 'purchase_completed',
};

/**
 * The ONLY properties that may ever reach GA4, and only on a purchase.
 * Everything else in a PostHog payload — audit_id, url_domain (hashed or
 * not), tier, score, cta, gateway — is dropped.
 */
const GA4_PURCHASE_KEYS = ['value', 'currency'];

const gtagFn = () =>
  (typeof window !== 'undefined' && typeof window.gtag === 'function' && window.gtag) || null;

/**
 * Build the GA4 payload by CONSTRUCTION, never by deletion.
 *
 * A delete-list ("strip audit_id") fails open: the day someone adds a new
 * property to a PostHog call, it ships to Google because nobody added it
 * to the list. Starting from {} and copying in only what is named fails
 * closed, which is the direction this has to fail in.
 *
 * A purchase with no trustworthy amount sends NO value rather than a
 * wrong one. PayPal's execute-payment returns no amount at all
 * (paypal_routes.py:479-484), and the 100% promo path redirects to the
 * success URL having charged nothing, so a hardcoded figure would be
 * fabricated revenue sitting in an analytics tool.
 */
function ga4Payload(ga4Name, properties) {
  if (ga4Name !== 'purchase_completed') return {};
  const out = {};
  for (const key of GA4_PURCHASE_KEYS) out[key] = properties[key];
  const okValue = typeof out.value === 'number' && Number.isFinite(out.value) && out.value > 0;
  const okCurrency = typeof out.currency === 'string' && /^[A-Z]{3}$/.test(out.currency);
  if (!okValue || !okCurrency) return {};
  return out;
}

function sendToGA4(event, properties) {
  const ga4Name = GA4_EVENTS[event];
  if (!ga4Name) return;
  const g = gtagFn();
  if (!g) return;
  try {
    g('event', ga4Name, ga4Payload(ga4Name, properties));
  } catch {
    /* analytics must never break the app */
  }
}

/**
 * FNV-1a 32-bit hash, hex-encoded. Stable across sessions so the same
 * audited domain groups together in PostHog without revealing it.
 */
export function hashDomain(url) {
  let host = '';
  try {
    host = new URL(url).hostname.replace(/^www\./, '').toLowerCase();
  } catch {
    return '';
  }
  let h = 0x811c9dc5;
  for (let i = 0; i < host.length; i++) {
    h ^= host.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(16).padStart(8, '0');
}

/**
 * Stitch events to the logged-in user. Call on every auth resolution —
 * PostHog dedupes repeat identify() calls for the same distinct id.
 * Intentionally excludes email: plan tier is all the funnel needs.
 */
export function identifyUser(user) {
  const ph = posthog();
  if (!ph || !user?.user_id) return;
  try {
    ph.identify(user.user_id, { plan: user.plan || 'FREE' });
  } catch {
    /* analytics must never break the app */
  }
}

export function resetIdentity() {
  const ph = posthog();
  if (!ph) return;
  try {
    ph.reset();
  } catch {
    /* ignore */
  }
}

export function track(event, properties = {}) {
  // GA4 first, and outside the PostHog guard: an ad blocker that kills
  // PostHog must not also silence GA4, and vice versa. They fail apart.
  sendToGA4(event, properties);

  const ph = posthog();
  if (!ph) return;
  try {
    ph.capture(event, properties);
  } catch {
    /* analytics must never break the app */
  }
}

/** Exported for the guard test only. Not for product code. */
export const __GA4_EVENTS = GA4_EVENTS;
